import {
  getActivationFlowData,
  saveActivationFlowData,
} from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import { clearPreloadedSimSecuritySelectionInSession } from "./preloaded-sim-activation-session-cascade";
import {
  frequencyKeyFromMonths,
  normalizePrimaryDataTierKey,
} from "./preloaded-sim-plan-mappers";

function getIncludedTierFrequencyAndPlanIdFromActivation(): {
  tier: string;
  frequency: string;
  includedPlanId: string | undefined;
} {
  const act = getActivationFlowData();
  const simData = act?.simData;
  if (!simData || typeof simData !== "object") {
    return { tier: "1gb", frequency: "monthly", includedPlanId: undefined };
  }
  const data = simData as Record<string, unknown>;
  const included = data.planIncluded;
  const includedPlan =
    included && typeof included === "object"
      ? (included as Record<string, unknown>)
      : null;
  const tier = normalizePrimaryDataTierKey(includedPlan?.name) || "1gb";
  const frequency = frequencyKeyFromMonths(includedPlan?.frequency);
  const rawId = includedPlan?._id;
  const includedPlanId = typeof rawId === "string" ? rawId : undefined;
  return { tier, frequency, includedPlanId };
}

function preloadedSimChoosePlanSelectionChanged(
  next: {
    preloadedSimPrimaryDataTier: string;
    preloadedSimServiceFrequency: string;
    preloadedSimPlanId: string | undefined;
    preloadedSimOptedForDataOrFrequencyUpsell: boolean;
  },
): boolean {
  const before = getActivationFlowData();
  return (
    before?.preloadedSimPrimaryDataTier !== next.preloadedSimPrimaryDataTier ||
    before?.preloadedSimServiceFrequency !== next.preloadedSimServiceFrequency ||
    (before?.preloadedSimPlanId ?? undefined) !==
      (next.preloadedSimPlanId ?? undefined) ||
    Boolean(before?.preloadedSimOptedForDataOrFrequencyUpsell) !==
      Boolean(next.preloadedSimOptedForDataOrFrequencyUpsell)
  );
}

/** Persists upgrade-card UI only; does not clear security (explore / draft). */
export function persistPreloadedSimUpgradeCardDraft(
  primaryDataTierKey: string,
  serviceFrequencyKey: string,
): void {
  const before = getActivationFlowData();
  const prev = before?.preloadedSimUpgradeCardDraft;
  if (
    prev?.primaryDataTier === primaryDataTierKey &&
    prev?.serviceFrequency === serviceFrequencyKey
  ) {
    return;
  }
  saveActivationFlowData({
    preloadedSimUpgradeCardDraft: {
      primaryDataTier: primaryDataTierKey,
      serviceFrequency: serviceFrequencyKey,
    },
  });
}

/**
 * User keeps included primary data and billing cadence from SIM (no upsell).
 * Does not change `preloadedSimUpgradeCardDraft` so step-2 upsell selects stay at
 * defaults (no draft) or the customer's last exploration picks.
 */
export function persistSimCardUpsellKeepIncludedAllocation(): void {
  const { tier, frequency, includedPlanId } =
    getIncludedTierFrequencyAndPlanIdFromActivation();
  const next = {
    preloadedSimPrimaryDataTier: tier,
    preloadedSimServiceFrequency: frequency,
    preloadedSimPlanId: includedPlanId,
    preloadedSimOptedForDataOrFrequencyUpsell: false,
  };
  if (!preloadedSimChoosePlanSelectionChanged(next)) return;
  saveActivationFlowData({
    preloadedSimPrimaryDataTier: next.preloadedSimPrimaryDataTier,
    preloadedSimServiceFrequency: next.preloadedSimServiceFrequency,
    preloadedSimPlanId: next.preloadedSimPlanId,
    preloadedSimOptedForDataOrFrequencyUpsell: next.preloadedSimOptedForDataOrFrequencyUpsell,
  });
  clearPreloadedSimSecuritySelectionInSession();
}

/** User-selected primary data tier and pay-every frequency (from upsell card). */
export function persistSimCardUpsellDataAndFrequencySelections(
  primaryDataTierKey: string,
  serviceFrequencyKey: string,
  planId: string | undefined,
  optedForUpsell: boolean,
): void {
  const next = {
    preloadedSimPrimaryDataTier: primaryDataTierKey,
    preloadedSimServiceFrequency: serviceFrequencyKey,
    preloadedSimPlanId: planId,
    preloadedSimOptedForDataOrFrequencyUpsell: optedForUpsell,
  };
  if (!preloadedSimChoosePlanSelectionChanged(next)) return;
  saveActivationFlowData({
    preloadedSimPrimaryDataTier: primaryDataTierKey,
    preloadedSimServiceFrequency: serviceFrequencyKey,
    preloadedSimPlanId: planId,
    preloadedSimOptedForDataOrFrequencyUpsell: optedForUpsell,
    ...(optedForUpsell
      ? {
          preloadedSimUpgradeCardDraft: {
            primaryDataTier: primaryDataTierKey,
            serviceFrequency: serviceFrequencyKey,
          },
        }
      : {}),
  });
  clearPreloadedSimSecuritySelectionInSession();
}

/** Tier/frequency only (intermediate UI); clears security when either value actually changes. */
export function persistPreloadedSimChoosePlanTierFrequencyOnly(
  primaryDataTierKey: string,
  serviceFrequencyKey: string,
): void {
  const before = getActivationFlowData();
  const changed =
    before?.preloadedSimPrimaryDataTier !== primaryDataTierKey ||
    before?.preloadedSimServiceFrequency !== serviceFrequencyKey;
  if (!changed) return;
  saveActivationFlowData({
    preloadedSimPrimaryDataTier: primaryDataTierKey,
    preloadedSimServiceFrequency: serviceFrequencyKey,
    preloadedSimUpgradeCardDraft: {
      primaryDataTier: primaryDataTierKey,
      serviceFrequency: serviceFrequencyKey,
    },
  });
  clearPreloadedSimSecuritySelectionInSession();
}
